import { type ReactNode, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  Info,
  Printer,
  RefreshCw,
} from "lucide-react";
import { ApiError } from "@/api/client";
import {
  getReportCaixasData,
  getReportMensalData,
  getReportPedidoData,
  getReportPedidoStores,
} from "@/features/supplier/api";
import {
  SUPPLIER_REPORT_STATUSES_KEY,
  useSupplierReportStatus,
} from "@/features/supplier/hooks";
import { ReportPrintFrame } from "@/components/report-print/ReportPrintFrame";
import { ReportPreview } from "@/components/report-print/ReportPreview";
import { ExportDialogFrame } from "@/components/report-print/ExportDialogFrame";
import { checkReportPrintReady } from "@/components/report-print/reportPrintGuard";
import type {
  CaixasExportDocument,
  MensalExportDocument,
  PedidoExportDocument,
  ReportReferenceStatus,
  Supplier,
  SupplierReportStatusParams,
} from "@/features/supplier/types";
import { todaySaoPaulo } from "@/lib/dates";
import { useDialogFocus } from "@/hooks/useDialogFocus";
import { CaixasPrintDocument } from "./CaixasPrintDocument";
import { MensalPrintDocument } from "./MensalPrintDocument";
import { PedidoPrintDocument } from "./PedidoPrintDocument";
import { ReportErrorMessage, type ReportErrorCode } from "./ReportErrorMessage";

export type ReportType = "mensal" | "caixas" | "pedido";

interface ReportModalError {
  code: ReportErrorCode;
  detail: string;
}

interface ReportModalProps {
  supplier: Supplier;
  type: ReportType;
  onClose: () => void;
  loadMensalData?: typeof getReportMensalData;
  loadCaixasData?: typeof getReportCaixasData;
  loadPedidoData?: typeof getReportPedidoData;
  loadPedidoStores?: typeof getReportPedidoStores;
  useReportStatus?: (
    supplierId: string,
    params: SupplierReportStatusParams,
  ) => {
    data?: ReportReferenceStatus;
    isLoading: boolean;
    refetch: () => Promise<unknown>;
  };
}

const REPORT_META = {
  mensal: {
    label: "Comissão Mensal",
  },
  caixas: {
    label: "Controle de Caixas",
  },
  pedido: {
    label: "Pedido do Dia",
  },
} as const;

function todayStr() {
  return todaySaoPaulo();
}

function currentMonthStr() {
  return todaySaoPaulo().slice(0, 7);
}

function firstOfMonthStr() {
  return `${todaySaoPaulo().slice(0, 7)}-01`;
}

function fmtDateTime(value?: string | null) {
  if (!value) return "Ainda não gerado";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function fmtMonth(value?: string | null) {
  if (!value) return "sem referência";
  const [year, month] = value.split("-");
  if (!year || !month) return value;
  return new Intl.DateTimeFormat("pt-BR", {
    month: "long",
    year: "numeric",
  }).format(new Date(Number(year), Number(month) - 1, 1));
}

function statusTone(
  type: ReportType,
  reportStatus?: {
    needs_export: boolean;
    stale_after_generation: boolean;
    ready_to_export: boolean;
    last_generated_at?: string | null;
  },
) {
  if (!reportStatus) {
    return {
      label: "Carregando status",
      color: "var(--login-muted)",
      background: "color-mix(in srgb, var(--login-muted) 12%, transparent)",
      border: "color-mix(in srgb, var(--login-muted) 28%, transparent)",
    };
  }
  if (reportStatus.stale_after_generation) {
    return {
      label: "Precisa reenviar",
      color: "#d97706",
      background: "rgba(217,119,6,0.12)",
      border: "rgba(217,119,6,0.28)",
    };
  }
  if (reportStatus.needs_export) {
    return {
      label: type === "mensal" ? "Pendente" : "Pronto para enviar",
      color: "#ef4444",
      background: "rgba(239,68,68,0.12)",
      border: "rgba(239,68,68,0.28)",
    };
  }
  if (reportStatus.ready_to_export || reportStatus.last_generated_at) {
    return {
      label: "Em dia",
      color: "#16a34a",
      background: "rgba(22,163,74,0.12)",
      border: "rgba(22,163,74,0.28)",
    };
  }
  return {
    label: "Sem movimento",
    color: "var(--login-muted)",
    background: "color-mix(in srgb, var(--login-muted) 12%, transparent)",
    border: "color-mix(in srgb, var(--login-muted) 28%, transparent)",
  };
}

export function ReportModal({
  supplier,
  type,
  onClose,
  loadMensalData = getReportMensalData,
  loadCaixasData = getReportCaixasData,
  loadPedidoData = getReportPedidoData,
  loadPedidoStores = getReportPedidoStores,
  useReportStatus = useSupplierReportStatus,
}: ReportModalProps) {
  const meta = REPORT_META[type];
  const mensalRequestRef = useRef(0);
  const caixasRequestRef = useRef(0);
  const pedidoRequestRef = useRef(0);
  const reportPrintFrameRef = useRef<HTMLIFrameElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const qc = useQueryClient();

  useDialogFocus(dialogRef, onClose);

  const [month, setMonth] = useState(currentMonthStr);
  const [commissionRate, setCommissionRate] = useState("");
  const [fromDate, setFromDate] = useState(firstOfMonthStr);
  const [toDate, setToDate] = useState(todayStr);
  const [date, setDate] = useState(todayStr);
  const [selectedStores, setSelectedStores] = useState<string[] | null>(null);
  const [mensalDocument, setMensalDocument] =
    useState<MensalExportDocument | null>(null);
  const [caixasDocument, setCaixasDocument] =
    useState<CaixasExportDocument | null>(null);
  const [pedidoDocument, setPedidoDocument] =
    useState<PedidoExportDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ReportModalError | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const statusParams = useMemo<SupplierReportStatusParams>(() => {
    if (type === "mensal") return { type, month };
    if (type === "pedido") return { type, date };
    return { type, from: fromDate, to: toDate };
  }, [type, month, date, fromDate, toDate]);

  const { data: reportStatus, isLoading: statusLoading } = useReportStatus(
    supplier.id,
    statusParams,
  );
  const {
    data: pedidoStores = [],
    isPending: storesLoading,
    isError: storesError,
  } = useQuery({
    queryKey: ["pedido-stores", supplier.id, date],
    queryFn: () => loadPedidoStores(supplier.id, date),
    enabled: type === "pedido" && Boolean(date),
  });

  async function fetchReport() {
    let mensalRequestId: number | null = null;
    let caixasRequestId: number | null = null;
    let pedidoRequestId: number | null = null;

    setLoading(true);
    setError(null);
    setToast(null);
    setMensalDocument(null);
    setCaixasDocument(null);
    setPedidoDocument(null);
    try {
      if (type === "mensal") {
        mensalRequestId = ++mensalRequestRef.current;
        const requestMonth = month;
        const requestCommissionRate = commissionRate;
        const document = await loadMensalData(
          supplier.id,
          requestMonth,
          requestCommissionRate,
        );

        if (isStaleMensalRequest(mensalRequestId)) {
          return;
        }

        setMensalDocument(document);
      } else if (type === "caixas") {
        caixasRequestId = ++caixasRequestRef.current;
        const requestFromDate = fromDate;
        const requestToDate = toDate;
        const document = await loadCaixasData(
          supplier.id,
          requestFromDate,
          requestToDate,
        );

        if (isStaleCaixasRequest(caixasRequestId)) {
          return;
        }

        setCaixasDocument(document);
      } else {
        pedidoRequestId = ++pedidoRequestRef.current;
        const requestDate = date;
        const document = await loadPedidoData(
          supplier.id,
          requestDate,
          selectedStores ?? undefined,
        );

        if (isStalePedidoRequest(pedidoRequestId)) {
          return;
        }

        setPedidoDocument(document);
        if (
          selectedStores !== null &&
          selectedStores.length < pedidoStores.length
        ) {
          setToast("Seleção registrada. As demais lojas continuam pendentes.");
        }
      }
      void qc
        .invalidateQueries({ queryKey: SUPPLIER_REPORT_STATUSES_KEY })
        .catch(() => undefined);
    } catch (e) {
      if (
        isStaleMensalRequest(mensalRequestId) ||
        isStaleCaixasRequest(caixasRequestId) ||
        isStalePedidoRequest(pedidoRequestId)
      ) {
        return;
      }

      if (
        type === "pedido" &&
        e instanceof ApiError &&
        e.code === "no_pedidos_for_date"
      ) {
        setToast("Nenhum pedido para esta data.");
        return;
      }
      setError({
        code: "EXP-GERAR",
        detail:
          "Seus filtros foram mantidos. Confira os dados e tente novamente.",
      });
    } finally {
      if (
        !isStaleMensalRequest(mensalRequestId) &&
        !isStaleCaixasRequest(caixasRequestId) &&
        !isStalePedidoRequest(pedidoRequestId)
      ) {
        setLoading(false);
      }
    }
  }

  function isStaleMensalRequest(requestId: number | null) {
    return requestId !== null && requestId !== mensalRequestRef.current;
  }

  function isStaleCaixasRequest(requestId: number | null) {
    return requestId !== null && requestId !== caixasRequestRef.current;
  }

  function isStalePedidoRequest(requestId: number | null) {
    return requestId !== null && requestId !== pedidoRequestRef.current;
  }

  function handleMensalMonthChange(value: string) {
    mensalRequestRef.current += 1;
    setMonth(value);
    setMensalDocument(null);
    setError(null);
    setToast(null);
    setLoading(false);
  }

  function handleCommissionRateChange(value: string) {
    mensalRequestRef.current += 1;
    setCommissionRate(value);
    setMensalDocument(null);
    setError(null);
    setToast(null);
    setLoading(false);
  }

  function handleCaixasFromDateChange(value: string) {
    caixasRequestRef.current += 1;
    setFromDate(value);
    setCaixasDocument(null);
    setError(null);
    setToast(null);
    setLoading(false);
  }

  function handleCaixasToDateChange(value: string) {
    caixasRequestRef.current += 1;
    setToDate(value);
    setCaixasDocument(null);
    setError(null);
    setToast(null);
    setLoading(false);
  }

  function handlePedidoDateChange(value: string) {
    pedidoRequestRef.current += 1;
    setDate(value);
    setSelectedStores(null);
    setPedidoDocument(null);
    setError(null);
    setToast(null);
    setLoading(false);
  }

  function toggleStore(id: string) {
    const current = selectedStores ?? pedidoStores.map((store) => store.id);
    setSelectedStores(
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id],
    );
    pedidoRequestRef.current += 1;
    setPedidoDocument(null);
    setError(null);
    setToast(null);
    setLoading(false);
  }

  function handlePrint() {
    const frameWindow = reportPrintFrameRef.current?.contentWindow;
    if (!frameWindow) {
      setError({
        code: "EXP-PRINT",
        detail: "A prévia ainda não está pronta. Gere o relatório novamente.",
      });
      return;
    }
    const ready = checkReportPrintReady(frameWindow.document);
    if (!ready.ok) {
      setError({ code: "EXP-PRINT", detail: ready.message });
      return;
    }
    frameWindow.focus();
    frameWindow.print();
  }

  const tone = statusTone(type, reportStatus);
  const hasDocument = Boolean(
    mensalDocument || caixasDocument || pedidoDocument,
  );
  const reportDocument: ReactNode = mensalDocument ? (
    <MensalPrintDocument document={mensalDocument} />
  ) : caixasDocument ? (
    <CaixasPrintDocument document={caixasDocument} />
  ) : pedidoDocument ? (
    <PedidoPrintDocument document={pedidoDocument} />
  ) : null;
  const reportNumber =
    mensalDocument?.number ?? caixasDocument?.number ?? pedidoDocument?.number;

  return (
    <>
      <style>{`
        @keyframes reportToastIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .report-panel-scroll {
          min-height: 0;
          overflow: auto;
          padding: 1rem;
          scrollbar-color: rgba(92, 104, 120, 0.36) rgba(92, 104, 120, 0.09);
          scrollbar-gutter: stable;
          scrollbar-width: thin;
        }
        .report-panel-scroll::-webkit-scrollbar {
          height: 8px;
          width: 8px;
        }
        .report-panel-scroll::-webkit-scrollbar-track {
          background: rgba(92, 104, 120, 0.09);
          border-radius: 4px;
        }
        .report-panel-scroll::-webkit-scrollbar-thumb {
          background: rgba(92, 104, 120, 0.36);
          border-radius: 4px;
        }
        .report-panel-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(92, 104, 120, 0.5);
        }
        .report-param-input {
          width: 100%;
          min-height: 2.75rem;
          padding: 0.7rem 0.85rem;
          background: var(--login-bg);
          border: 1px solid var(--login-border);
          border-radius: 4px;
          color: var(--login-text);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.9375rem;
          outline: none;
        }
        .report-param-input:focus {
          border-color: var(--login-accent);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--login-accent) 16%, transparent);
        }
        .report-primary-btn,
        .report-secondary-btn {
          transition: opacity 0.16s ease, background-color 0.16s ease;
        }
        @media (hover: hover) {
          .report-primary-btn:hover:not(:disabled),
          .report-secondary-btn:hover:not(:disabled) { filter: brightness(0.96); }
        }
        .report-primary-btn:disabled,
        .report-secondary-btn:disabled { opacity: 0.55; cursor: not-allowed; }
        .report-iframe-spin { animation: spin 0.8s linear infinite; }
        .report-print-frame {
          background: #eef2f7;
          border: 0;
          border-radius: 4px;
          display: block;
          height: min(72vh, 820px);
          width: 100%;
        }
        @media print {
          .report-overlay { display: none !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          .report-primary-btn,
          .report-secondary-btn,
          .report-iframe-spin,
          .report-toast {
            animation: none !important;
            transition-duration: 0.01ms !important;
            transform: none !important;
          }
        }
      `}</style>

      <ExportDialogFrame
        titleId="supplier-report-modal-title"
        eyebrow={meta.label}
        title={supplier.name}
        onClose={onClose}
        dialogRef={dialogRef}
        overlayClassName="report-overlay"
        panelClassName="report-panel"
      >
        <div
          className="report-panel-scroll"
          style={{
            display: "grid",
            gap: "1rem",
            minHeight: 0,
            overflow: "auto",
            padding: "1rem",
          }}
        >
          <StatusBox tone={tone} type={type} reportStatus={reportStatus} />

          <div style={{ display: "grid", gap: "0.75rem" }}>
            {type === "mensal" && (
              <>
                <Field label="Mês de referência">
                  <input
                    className="report-param-input"
                    type="month"
                    value={month}
                    onInput={(e) =>
                      handleMensalMonthChange(e.currentTarget.value)
                    }
                    onChange={(e) => handleMensalMonthChange(e.target.value)}
                  />
                </Field>
                <Field label="Comissão (%)">
                  <input
                    className="report-param-input"
                    type="text"
                    inputMode="decimal"
                    value={commissionRate}
                    onInput={(event) =>
                      handleCommissionRateChange(event.currentTarget.value)
                    }
                    onChange={(event) =>
                      handleCommissionRateChange(event.target.value)
                    }
                    aria-label="Comissão do relatório mensal em porcentagem"
                  />
                </Field>
              </>
            )}
            {type === "caixas" && (
              <>
                <Field label="Período inicial">
                  <input
                    className="report-param-input"
                    type="date"
                    value={fromDate}
                    onInput={(e) =>
                      handleCaixasFromDateChange(e.currentTarget.value)
                    }
                    onChange={(e) => handleCaixasFromDateChange(e.target.value)}
                  />
                </Field>
                <Field label="Período final">
                  <input
                    className="report-param-input"
                    type="date"
                    value={toDate}
                    onInput={(e) =>
                      handleCaixasToDateChange(e.currentTarget.value)
                    }
                    onChange={(e) => handleCaixasToDateChange(e.target.value)}
                  />
                </Field>
              </>
            )}
            {type === "pedido" && (
              <>
                <Field label="Data do pedido">
                  <input
                    className="report-param-input"
                    type="date"
                    value={date}
                    onInput={(e) =>
                      handlePedidoDateChange(e.currentTarget.value)
                    }
                    onChange={(e) => handlePedidoDateChange(e.target.value)}
                  />
                </Field>
                <details className="report-store-picker">
                  <summary>
                    <span>Lojas</span>{" "}
                    <strong>
                      {storesLoading
                        ? "Carregando..."
                        : storesError
                          ? "Indisponível"
                          : selectedStores === null
                            ? "Todas"
                            : `${selectedStores.length} de ${pedidoStores.length}`}
                    </strong>
                  </summary>
                  <fieldset aria-label="Lojas incluídas">
                    <div className="report-store-options">
                      {storesLoading ? (
                        "Carregando lojas..."
                      ) : storesError ? (
                        "Não foi possível carregar as lojas."
                      ) : pedidoStores.length === 0 ? (
                        "Sem pedidos para esta data."
                      ) : (
                        <>
                          <label className="report-store-option report-store-option--all">
                            <input
                              type="checkbox"
                              checked={selectedStores === null}
                              onChange={() => {
                                setSelectedStores(
                                  selectedStores === null ? [] : null,
                                );
                                pedidoRequestRef.current += 1;
                                setPedidoDocument(null);
                                setError(null);
                              }}
                            />{" "}
                            Todas as lojas
                          </label>
                          {pedidoStores.map((store) => (
                            <label
                              key={store.id}
                              className="report-store-option"
                            >
                              <input
                                type="checkbox"
                                checked={
                                  selectedStores === null ||
                                  selectedStores.includes(store.id)
                                }
                                onChange={() => toggleStore(store.id)}
                              />
                              <span>
                                {store.client_name} · {store.name}
                              </span>
                            </label>
                          ))}
                        </>
                      )}
                    </div>
                  </fieldset>
                </details>
              </>
            )}
          </div>

          <InfoRow
            label={
              type === "pedido" ? "Última geração completa" : "Última geração"
            }
            value={
              statusLoading
                ? "Carregando..."
                : fmtDateTime(reportStatus?.last_generated_at)
            }
          />
          {reportStatus?.latest_relevant_change_at && (
            <InfoRow
              label="Última mudança relevante"
              value={fmtDateTime(reportStatus.latest_relevant_change_at)}
            />
          )}
          {reportStatus?.last_generated_range_from &&
            reportStatus?.last_generated_range_to && (
              <InfoRow
                label="Faixa da última emissão"
                value={`${reportStatus.last_generated_range_from} até ${reportStatus.last_generated_range_to}`}
              />
            )}

          {toast && <ToastMessage message={toast} />}
          {error && (
            <ReportErrorMessage code={error.code} detail={error.detail} />
          )}

          {reportDocument && (
            <div className="report-preview-pane" aria-label="Prévia do PDF">
              <ReportPrintFrame
                frameRef={reportPrintFrameRef}
                title={`Prévia do relatório ${reportNumber ?? supplier.name}`}
              >
                <ReportPreview>{reportDocument}</ReportPreview>
              </ReportPrintFrame>
            </div>
          )}

          <div style={{ display: "grid", gap: "0.65rem" }}>
            <button
              type="button"
              className="report-primary-btn"
              onClick={fetchReport}
              disabled={
                loading ||
                (type === "pedido" &&
                  (storesLoading ||
                    storesError ||
                    pedidoStores.length === 0 ||
                    selectedStores?.length === 0))
              }
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.55rem",
                minHeight: "2.75rem",
                border: "none",
                borderRadius: "4px",
                background: "var(--login-accent)",
                color: "#fff",
                fontFamily: "'Barlow Condensed', sans-serif",
                fontWeight: 700,
                fontSize: "1rem",
                letterSpacing: "0.06em",
                cursor: "pointer",
              }}
            >
              {loading ? (
                <RefreshCw size={15} className="report-iframe-spin" />
              ) : (
                <RefreshCw size={15} />
              )}
              {loading
                ? "Gerando..."
                : error
                  ? "Tentar novamente"
                  : "Gerar e registrar"}
            </button>

            {hasDocument && (
              <button
                type="button"
                className="report-secondary-btn"
                onClick={handlePrint}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.55rem",
                  minHeight: "2.75rem",
                  borderRadius: "4px",
                  border:
                    "1px solid color-mix(in srgb, var(--login-accent) 38%, var(--login-border))",
                  background: "transparent",
                  color: "var(--login-accent)",
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  letterSpacing: "0.06em",
                  cursor: "pointer",
                }}
              >
                <Printer size={15} />
                Imprimir / PDF
              </button>
            )}
          </div>
        </div>
      </ExportDialogFrame>
    </>
  );
}

function StatusBox({
  tone,
  type,
  reportStatus,
}: {
  tone: ReturnType<typeof statusTone>;
  type: ReportType;
  reportStatus?: {
    reference_month?: string | null;
    last_generated_at?: string | null;
    needs_export: boolean;
    stale_after_generation: boolean;
    ready_to_export: boolean;
  };
}) {
  return (
    <div
      style={{
        padding: "0.2rem 0 0.2rem 0.8rem",
        borderLeft: `3px solid ${tone.color}`,
      }}
    >
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.4rem",
          color: tone.color,
          fontFamily: "'Barlow Condensed', sans-serif",
          fontSize: "0.86rem",
          fontWeight: 700,
          letterSpacing: "0.06em",
        }}
      >
        {reportStatus?.needs_export || reportStatus?.stale_after_generation ? (
          <AlertCircle size={15} />
        ) : (
          <CheckCircle2 size={15} />
        )}
        {tone.label}
      </div>
      <p
        style={{
          margin: "0.45rem 0 0",
          color: "var(--login-text)",
          fontSize: "0.875rem",
          lineHeight: 1.45,
        }}
      >
        {type === "mensal" &&
          (reportStatus?.needs_export
            ? `O mês ${fmtMonth(reportStatus.reference_month)} ainda precisa ser emitido.`
            : `Última emissão: ${fmtDateTime(reportStatus?.last_generated_at)}.`)}
        {type === "pedido" &&
          (reportStatus?.stale_after_generation
            ? "Houve alteração relevante depois da última emissão. Gere e reenvie."
            : reportStatus?.needs_export
              ? "Todos os pedidos já fecharam para esse fornecedor. Gere e envie."
              : reportStatus?.ready_to_export
                ? `Última emissão: ${fmtDateTime(reportStatus?.last_generated_at)}.`
                : "Ainda existem pedidos desse fornecedor sem fechamento suficiente.")}
        {type === "caixas" &&
          `Última emissão registrada: ${fmtDateTime(reportStatus?.last_generated_at)}.`}
      </p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "grid", gap: "0.4rem" }}>
      <span
        style={{
          fontFamily: "'Barlow Condensed', sans-serif",
          fontWeight: 700,
          fontSize: "0.78rem",
          letterSpacing: "0.08em",
          color: "var(--login-muted)",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        padding: "0.65rem 0",
        borderBottom: "1px solid var(--login-border)",
      }}
    >
      <div
        style={{
          fontFamily: "'Barlow Condensed', sans-serif",
          fontWeight: 700,
          fontSize: "0.72rem",
          letterSpacing: "0.08em",
          color: "var(--login-muted)",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: "0.28rem",
          color: "var(--login-text)",
          fontSize: "0.9rem",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function ToastMessage({ message }: { message: string }) {
  return (
    <div
      className="report-toast"
      role="status"
      aria-live="polite"
      style={{
        animation: "reportToastIn 0.18s ease",
        display: "flex",
        gap: "0.6rem",
        alignItems: "center",
        padding: "0.8rem 0.9rem",
        borderRadius: "4px",
        borderLeft: "3px solid #4a9eff",
        background: "rgba(74,158,255,0.08)",
        color: "var(--login-text)",
        fontSize: "0.9rem",
      }}
    >
      <Info size={16} color="#4a9eff" />
      {message}
    </div>
  );
}
